/**
 * `createApiClient` — o objeto que as duas SPAs recebem, e a unica porta de saida HTTP do
 * produto (ADR-0001).
 *
 * ```ts
 * const api = createApiClient({
 *   baseUrl: import.meta.env.VITE_API_URL,
 *   client: { id: 'web-cliente', version: __APP_VERSION__ },
 *   onSessionLost: () => clearUserScopedStorage('REVOKED'),   // PWA-034
 * });
 * ```
 *
 * Repare no que **nao** ha na chamada: token, header, funcao de CSRF, gerador de chave de
 * idempotencia. Tudo isso tem default correto, e o default e a decisao dos documentos.
 */
import { resolveConfig, type ApiClientConfig, type ResolvedApiClientConfig } from './config.js';
import type { ApiClientCore } from './core.js';
import { createAddressesApi, type AddressesApi } from './endpoints/addresses.js';
import { createAuthApi, type AuthApi } from './endpoints/auth.js';
import { createCartApi, type CartApi } from './endpoints/cart.js';
import { createCheckoutApi, createOrdersApi, type CheckoutApi, type OrdersApi } from './endpoints/orders.js';
import {
  createCatalogApi,
  createDeliveryApi,
  createStoresApi,
  type CatalogApi,
  type DeliveryApi,
  type StoresApi,
} from './endpoints/public.js';
import { createStorePanelApi, type StorePanelApi } from './endpoints/store.js';
import { performRequest, type ApiRequestSpec, type ApiResponse } from './http.js';
import { IdempotencyKeeper } from './idempotency.js';
import { ResourceVersionStore } from './versions.js';

export interface ApiClient extends ApiClientCore {
  readonly config: ResolvedApiClientConfig;
  readonly idempotency: IdempotencyKeeper;

  readonly auth: AuthApi;
  readonly stores: StoresApi;
  readonly catalog: CatalogApi;
  readonly delivery: DeliveryApi;
  readonly cart: CartApi;
  readonly checkout: CheckoutApi;
  readonly orders: OrdersApi;
  readonly addresses: AddressesApi;
  readonly storePanel: StorePanelApi;

  /**
   * Esquece **o que este pacote guarda**: versoes de recurso e chaves de idempotencia.
   *
   * Nao e o `clearUserScopedStorage` do `PWA §7.5` — aquele tem sete passos (chaves cifradas,
   * object stores, caches `api-*`, push, `BroadcastChannel`, `postMessage` ao SW,
   * `localStorage`) e e da aplicacao, que e quem conhece IndexedDB e Cache Storage. Este e o
   * pedaco que cabe ao cliente HTTP, e ele existe para que a aplicacao nao precise saber que
   * existem um cofre de `ETag` e um de chaves aqui dentro.
   *
   * Chame-o no logout, na troca de conta e em `401 SESSION_REVOKED`: um `ETag` de carrinho do
   * titular anterior produziria `409` na primeira acao do titular novo.
   */
  resetSessionState: () => void;
}

export function createApiClient(config: ApiClientConfig): ApiClient {
  const resolved = resolveConfig(config);
  const versions = new ResourceVersionStore();
  const idempotency = new IdempotencyKeeper(resolved.generateIdempotencyKey);

  const request = <TResponse,>(spec: ApiRequestSpec<TResponse>): Promise<ApiResponse<TResponse>> =>
    performRequest(resolved, idempotency, spec);

  const core: ApiClientCore = { request, versions };

  return {
    config: resolved,
    versions,
    idempotency,
    request,

    auth: createAuthApi(core),
    stores: createStoresApi(core),
    catalog: createCatalogApi(core),
    delivery: createDeliveryApi(core),
    cart: createCartApi(core),
    checkout: createCheckoutApi(core),
    orders: createOrdersApi(core),
    addresses: createAddressesApi(core),
    storePanel: createStorePanelApi(core),

    resetSessionState() {
      versions.clear();
      idempotency.clear();
    },
  };
}
